// Splat material binding (SPLATS-MATERIAL.md Task 5).
//
// A Gaussian-splat character has no rig mesh slots, so we bind a graph material
// to it through SENTINEL slot names in the existing `material_mapping` jsonb —
// no schema migration, reusing `putMaterialMapping` and the content-browser
// drag-to-bind. Two slots:
//   __splat__ — the lifted face/body splat appearance
//   __hair__  — the relightable hair splat
//
// This module is the PURE, unit-tested core: resolve which graph material is
// bound to each slot, and produce an immutable mapping update when the user
// binds/clears a slot. The inspector UI (RigInspector) and the runtime read
// (OGSModel) consume these — see the doc.
//
// The sentinel entries are harmless to the polygonal material path: AvatarModel
// doesn't mount for splat characters, and even if these rows reached
// `applyMaterialMapping`, their meshName never matches a real rig node, so they
// bind nothing.

/** Sentinel meshName for the lifted face/body splat appearance slot. */
export const SPLAT_SLOT = '__splat__'
/** Sentinel meshName for the hair splat slot. */
export const HAIR_SLOT = '__hair__'

// Out-of-range meshIndex values so a freshly-created sentinel row never collides
// with a real mesh slot (those are 0..N-1) and the two render in a stable order.
const SLOT_INDEX = { [SPLAT_SLOT]: -2, [HAIR_SLOT]: -1 }

/** The two splat slots, in display order, with human labels for the inspector. */
export const SPLAT_SLOTS = [
  { meshName: SPLAT_SLOT, label: 'Splat appearance' },
  { meshName: HAIR_SLOT, label: 'Hair' },
]

/** True if a material_assets row is a node-graph material (the only kind a splat
 *  slot accepts — splats shade through the compiled graph, not a fixed PBR/toon
 *  material). */
export function isGraphMaterialAsset(asset) {
  return !!asset && asset.type === 'graph'
}

/**
 * Resolve the graph material bound to each splat sentinel slot.
 *
 * @param {Array<{meshName?:string, materialId?:string}>} mapping - material_mapping rows.
 * @param {Array<{id:string, type?:string, data?:object}>} materials - material_assets rows.
 * @returns {{ splat: Binding|null, hair: Binding|null }} where
 *   Binding = { materialId, asset|null, isGraph } — asset is null if the bound
 *   id has no matching material row (stale binding); isGraph flags a non-graph
 *   mistake so the consumer can warn rather than silently mis-shade.
 */
export function resolveSplatMaterials(mapping, materials) {
  const byId = new Map((materials ?? []).map((m) => [m.id, m]))
  const pick = (slot) => {
    const entry = (mapping ?? []).find((e) => e && e.meshName === slot)
    const id = entry?.materialId
    if (!id) return null
    const asset = byId.get(id) ?? null
    return { materialId: id, asset, isGraph: isGraphMaterialAsset(asset) }
  }
  return { splat: pick(SPLAT_SLOT), hair: pick(HAIR_SLOT) }
}

/**
 * Immutably set (or clear) the material bound to a splat sentinel slot.
 * Creates the sentinel row if absent; passing a falsy `materialId` clears the
 * binding (strips `materialId`, keeps the row) — same shape RigInspector's mesh
 * bind produces. Returns a NEW array; the input is untouched.
 *
 * @param {Array} mapping - current material_mapping rows.
 * @param {string} slotMeshName - SPLAT_SLOT or HAIR_SLOT.
 * @param {string|null} materialId - material to bind, or null/'' to clear.
 */
export function setSplatSlotMaterial(mapping, slotMeshName, materialId) {
  if (slotMeshName !== SPLAT_SLOT && slotMeshName !== HAIR_SLOT) {
    throw new Error(`setSplatSlotMaterial: not a splat slot: ${slotMeshName}`)
  }
  const base = mapping ?? []
  const applied = (entry) => {
    if (materialId) return { ...entry, materialId }
    const { materialId: _drop, ...rest } = entry
    return rest
  }
  if (base.some((e) => e && e.meshName === slotMeshName)) {
    return base.map((e) => (e && e.meshName === slotMeshName ? applied(e) : e))
  }
  return [...base, applied({ meshIndex: SLOT_INDEX[slotMeshName], meshName: slotMeshName })]
}
